import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Logo } from '../components/Logo';
import PageTitle from '../components/PageTitle';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Play, 
  Search, 
  Filter,
  Clock,
  Star,
  Flame,
  Target,
  Zap,
  BookOpen,
  TrendingUp,
  ChevronRight,
  X,
  LogOut,
  User,
  Settings,
  Gift
} from 'lucide-react';

const ClassBrowser = () => {
  const { api, user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [classes, setClasses] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [difficulty, setDifficulty] = useState(searchParams.get('difficulty') || '');
  const [duration, setDuration] = useState(searchParams.get('duration') || '');
  const [machineType, setMachineType] = useState(searchParams.get('machine') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'created_at');

  const categoryIcons = {
    full_body: <Flame className="w-5 h-5" />,
    core: <Target className="w-5 h-5" />,
    lower_body: <TrendingUp className="w-5 h-5" />,
    upper_body: <Zap className="w-5 h-5" />,
    express: <Zap className="w-5 h-5" />,
    fundamentals: <BookOpen className="w-5 h-5" />
  };

  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      setLoading(true);
      try {
        const [classesRes, categoriesRes] = await Promise.all([
          api.get('/classes/on-demand', {
            params: {
              category: category || undefined,
              difficulty: difficulty || undefined,
              duration_max: duration ? parseInt(duration) : undefined,
              search: search || undefined,
              sort_by: sortBy,
              limit: 50
            }
          }),
          api.get('/classes/categories')
        ]);

        if (isMounted) {
          setClasses(classesRes.data || []);
          setCategories(categoriesRes.data || {});
        }
      } catch (error) {
        console.error('Failed to fetch classes:', error);
        if (isMounted) {
          setClasses([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, [api, category, difficulty, duration, search, sortBy]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearch('');
    setCategory('');
    setDifficulty('');
    setDuration('');
    setMachineType('');
    setSortBy('created_at');
    setSearchParams(new URLSearchParams());
  };

  const hasFilters = search || category || difficulty || duration || machineType;

  return (
    <div className="min-h-screen bg-[#050505]">
      <PageTitle title="Classes" description="Browse on-demand and live Lagree classes taught by certified instructors." />
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo linkTo="/dashboard" size="default" />

          <nav className="hidden md:flex items-center gap-6">
            <Link to="/dashboard" className="text-zinc-400 hover:text-white transition-colors">Home</Link>
            <Link to="/classes" className="text-white font-medium">Classes</Link>
            <Link to="/programs" className="text-zinc-400 hover:text-white transition-colors">Programs</Link>
            <Link to="/shop" className="text-zinc-400 hover:text-white transition-colors">Shop</Link>
            <Link to="/instructors" className="text-zinc-400 hover:text-white transition-colors">Instructors</Link>
          </nav>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                <div className="w-8 h-8 bg-teal-400 rounded-full flex items-center justify-center text-sm font-bold text-black">
                  {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                </div>
                <span className="hidden md:inline text-zinc-300">{user?.first_name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-[#121212] border-zinc-800">
              <DropdownMenuItem asChild>
                <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                  <User className="w-4 h-4" />
                  Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/referrals" className="flex items-center gap-2 cursor-pointer text-teal-400">
                  <Gift className="w-4 h-4" />
                  Refer & Earn $50
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                  <Settings className="w-4 h-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="flex items-center gap-2 text-red-400 cursor-pointer"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">CLASS LIBRARY</h1>
          <p className="text-zinc-500">Explore our collection of {Object.values(categories).reduce((sum, c) => sum + (c.count || 0), 0)}+ classes</p>
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-3 mb-8">
          <button
            onClick={() => { setCategory(''); updateFilter('category', ''); }}
            className={`px-4 py-2 rounded-sm font-medium text-sm transition-colors ${
              !category ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'
            }`}
            data-testid="filter-all"
          >
            All Classes
          </button>
          {Object.entries(categories).map(([key, cat]) => (
            <button
              key={key}
              onClick={() => { setCategory(key); updateFilter('category', key); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-sm font-medium text-sm transition-colors ${
                category === key ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'
              }`}
              data-testid={`filter-${key}`}
            >
              {categoryIcons[key]}
              {cat.name}
              <span className="text-xs opacity-60">({cat.count})</span>
            </button>
          ))}
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-600" />
            <Input
              type="text"
              placeholder="Search classes..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); updateFilter('search', e.target.value); }}
              className="pl-10 bg-[#121212] border-zinc-800 h-12 text-white placeholder:text-zinc-600"
              data-testid="search-input"
            />
          </div>

          <Select value={difficulty} onValueChange={(v) => { setDifficulty(v); updateFilter('difficulty', v); }}>
            <SelectTrigger className="w-full md:w-[180px] bg-[#121212] border-zinc-800 h-12" data-testid="filter-difficulty">
              <SelectValue placeholder="Difficulty" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-zinc-800">
              <SelectItem value="beginner">Beginner</SelectItem>
              <SelectItem value="intermediate">Intermediate</SelectItem>
              <SelectItem value="advanced">Advanced</SelectItem>
            </SelectContent>
          </Select>

          <Select value={duration} onValueChange={(v) => { setDuration(v); updateFilter('duration', v); }}>
            <SelectTrigger className="w-full md:w-[180px] bg-[#121212] border-zinc-800 h-12" data-testid="filter-duration">
              <SelectValue placeholder="Duration" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-zinc-800">
              <SelectItem value="20">Under 20 min</SelectItem>
              <SelectItem value="30">Under 30 min</SelectItem>
              <SelectItem value="45">Under 45 min</SelectItem>
              <SelectItem value="60">Under 60 min</SelectItem>
            </SelectContent>
          </Select>

          <Select value={machineType} onValueChange={(v) => { setMachineType(v); updateFilter('machine', v); }}>
            <SelectTrigger className="w-full md:w-[180px] bg-[#121212] border-zinc-800 h-12" data-testid="filter-machine">
              <SelectValue placeholder="Machine Type" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-zinc-800">
              <SelectItem value="megaformer">Megaformer</SelectItem>
              <SelectItem value="micro">Lagree Micro</SelectItem>
              <SelectItem value="mini-pro">Mini Pro</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={(v) => { setSortBy(v); updateFilter('sort', v); }}>
            <SelectTrigger className="w-full md:w-[180px] bg-[#121212] border-zinc-800 h-12" data-testid="filter-sort">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-zinc-800">
              <SelectItem value="created_at">Newest</SelectItem>
              <SelectItem value="rating">Top Rated</SelectItem>
              <SelectItem value="duration_minutes">Duration</SelectItem>
            </SelectContent>
          </Select>

          {hasFilters && (
            <Button
              variant="ghost"
              onClick={clearFilters}
              className="text-zinc-400 hover:text-white"
              data-testid="clear-filters"
            >
              <X className="w-4 h-4 mr-2" />
              Clear
            </Button>
          )}
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-zinc-500">
            {classes.length} classes found
          </p>
        </div>

        {/* Classes Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-[#121212] rounded-sm overflow-hidden">
                <div className="aspect-video skeleton" />
                <div className="p-4 space-y-3">
                  <div className="h-4 skeleton rounded w-1/3" />
                  <div className="h-5 skeleton rounded w-3/4" />
                  <div className="h-4 skeleton rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : classes.length === 0 ? (
          <div className="text-center py-20">
            <Filter className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">No classes found</h3>
            <p className="text-zinc-500 mb-6">Try adjusting your filters or search</p>
            <Button onClick={clearFilters} className="bg-white text-black">
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {classes.map((cls) => (
              <Link 
                key={cls.id} 
                to={`/class/${cls.id}`}
                className="group bg-[#121212] border border-zinc-800 rounded-sm overflow-hidden hover:border-zinc-700 transition-all card-hover"
                data-testid={`class-card-${cls.id}`}
              >
                <div className="relative aspect-video">
                  <img 
                    src={cls.thumbnail_url || 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?q=85&w=800&auto=format&fit=crop'} 
                    alt={cls.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center">
                      <Play className="w-6 h-6 text-black fill-black ml-1" />
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 rounded-sm text-xs flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {cls.duration_minutes} min
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded-sm text-xs font-medium badge-${cls.category}`}>
                      {cls.category.replace('_', ' ')}
                    </span>
                    <span className={`text-xs difficulty-${cls.difficulty}`}>
                      {cls.difficulty}
                    </span>
                  </div>
                  <h3 className="font-bold mb-1 line-clamp-1">{cls.title}</h3>
                  <p className="text-zinc-500 text-sm line-clamp-2 mb-3">{cls.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-sm">{cls.instructor_name}</span>
                    {cls.rating > 0 && (
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        <span className="text-sm">{cls.rating.toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ClassBrowser;
